import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormErrorText,
  FormField,
  FormFieldLayout,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import type { SelectMenuItem, SelectValue } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LabeledValue } from 'antd/es/select'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { CampaignTypeDto } from 'api/Trading-Accounts/Types/CampaignTypeDto'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import {
  getMarketingCampaignName,
  MarketingCampaignType,
} from 'api/Trading-Accounts/Types/MarketingCampaignType'
import { isMT5Platform, PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { AddUpdateMarketingCampaign } from 'api/Transaction/Types/AddUpdateMarketingCampaign'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { CheckboxSwitch } from 'components/checkboxSwitch/CheckboxSwitch'
import { Form, FormikErrors, FormikProps } from 'formik'
import { AuditLogIcon } from 'icons/AuditLogIcon'
import { ChevronDown } from 'lucide-react'
import { FormSubmitValues, UrlPattern } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'
import { isNumeric } from 'utils/is-numeric'
import { isString } from 'utils/is-string'
import { hasPermission } from 'utils/permissionsValidator'
import { utcToZonedTime, zonedTimeToUtc } from 'utils/time-zone/helpers'
import { withCustomFormik } from 'utils/withAppFormik'
import { v4 as uuid } from 'uuid'

import { useApiClient } from '../../../utils/api/ApiClient'
import { TradingAccountsApiClient } from '../../../utils/api/tradingAccountsApi'
import { isMarginBonusType } from '../AddCampaignDetailsForm'
import { CampaignDetailsBusinessRuleModal } from './CampaignDetailsBusinessRuleModal'
import {
  BusinessRule,
  CampaignDetailsBusinessRulesTable,
} from './CampaignDetailsBusinessRulesTable'
import { CampaignDetailsCurrencyModal } from './CampaignDetailsCurrencyModal'
import { CampaignDetailsCurrencyTable } from './CampaignDetailsCurrencyTable'
import {
  CampaignRebateConfiguration,
  CampaignRebateConfigurationModal,
} from './CampaignRebateConfigurationModal/CampaignRebateConfigurationModal'
import { CampaignRebateConfigurationTable } from './CampaignRebateConfigurationTable'

export interface CampaignDetailsFormValues {
  id?: string
  name: string
  key: string
  typeId: number
  calculationTypeId: number | undefined
  to: Date | null
  from: Date | null
  enrollmentTo: Date | null
  disqualificationEmailTemplate: string | null
  termsAndConditionsUrl: string | null
  defaultCurrencyId: CurrencyType
  currencies: LabeledValue[]
  currenciesMinDeposit: Record<CurrencyType, number>
  statusId: number
  intervalId: number
  symbols: string[]
  rebates: CampaignRebateConfiguration[]
  minDeposit: number | undefined
  maxPayout: number | undefined
  intervalValue: number
  calculatePayouts: boolean
  issuePayouts: boolean
  payoutIntervalId: number
  approvePayouts: boolean
  tickmillCompanies: number[]
  platformTypes: number[]
  restrictions: number[]
  businessRules: BusinessRule[]
  luckyDrawConfiguration: {
    expirationIntervalId: number
    expirationIntervalValue: number
    maxDailyUsableDrawChances: number | undefined
    maxDailyGeneratedDrawChances: number | undefined
    maxDrawChancesSingleTransaction: number | undefined
    maxWinChancesPerClient: number | undefined
    maxWeeklyMaximumRewardsAllowed: number | undefined
    minValueAllowance: number
    campaignTimeZoneId: string
    maxPayoutDivisionPerDay: number | null
  }
  marginBonusConfiguration: {
    campaignMarginBonusTiers: { minDeposit: number; bonusAwardedPercentage: number }[]
    maximumBonus: number | null | undefined
    campaignMarginBonusWithdrawRuleId: number | null | undefined
    removeCreditOnStopOut: boolean | undefined
    validForDays: number | null | undefined
  }
  useCustomCurrencyAmount: boolean
  campaignDescription: string
  internalOnly: boolean
}

type CampaignDetailsFormProps = FormikProps<CampaignDetailsFormValues> & OuterProps

interface ModalDataProps<T> {
  data: T | undefined
  visible: boolean
}

const useCurrencyModal = (props: CampaignDetailsFormProps) => {
  const { initialValues, setFieldValue } = props

  const [isEditCurrencyModalOpen, setEditCurrencyModalOpen] = useState<boolean>(false)

  const handleCurrency = (event: ChangeEvent<HTMLInputElement>, labeledValue: LabeledValue) => {
    const value = event.target.value
    const minDeposit = value ? Number(value) : null
    setFieldValue(`currenciesMinDeposit.${labeledValue.value}`, minDeposit)
  }

  const handleCurrencyModalOpen = () => {
    setEditCurrencyModalOpen(true)
  }

  const handleCurrencyModalClose = () => {
    setFieldValue('currenciesMinDeposit', initialValues.currenciesMinDeposit)
    setEditCurrencyModalOpen(false)
  }

  const handleSubmitCurrencies = (event: FormEvent) => {
    event.preventDefault()
    setEditCurrencyModalOpen(false)
  }

  return {
    isEditCurrencyModalOpen,
    setEditCurrencyModalOpen,
    handleCurrency,
    handleCurrencyModalOpen,
    handleCurrencyModalClose,
    handleSubmitCurrencies,
  }
}

const useCampaignBusinessRuleModal = (props: CampaignDetailsFormProps) => {
  const { values, setFieldValue } = props

  const initialModalDataState = {
    data: undefined,
    visible: false,
  }

  const [campaignBusinessRuleModal, setCampaignBusinessRuleModal] =
    useState<ModalDataProps<BusinessRule>>(initialModalDataState)

  const handleCampaignBusinessRuleSubmit = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const isNew = !values.businessRules.find((x) => x?.id === businessRule?.id)
      const newBusinessRules = isNew
        ? values.businessRules.concat(businessRule)
        : values.businessRules.map((x) => (x.id === businessRule.id ? businessRule : x))
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalRemove = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const newBusinessRules = values.businessRules.filter((x) => x.id !== businessRule.id)
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalOpen = (businessRule?: BusinessRule) => {
    setCampaignBusinessRuleModal({
      data: businessRule,
      visible: true,
    })
  }

  const handleCampaignBusinessRuleModalClose = () => {
    setCampaignBusinessRuleModal(initialModalDataState)
  }

  return {
    data: campaignBusinessRuleModal,
    handleCampaignBusinessRuleSubmit,
    handleCampaignBusinessRuleModalOpen,
    handleCampaignBusinessRuleModalRemove,
    handleCampaignBusinessRuleModalClose,
  }
}

const useCampaignRebateConfigurationModal = (props: CampaignDetailsFormProps) => {
  const { values, setFieldValue } = props

  const initialModalDataState = {
    data: undefined,
    visible: false,
  }

  const [campaignRebateConfigurationModal, setCampaignRebateConfigurationModal] =
    useState<ModalDataProps<CampaignRebateConfiguration>>(initialModalDataState)

  const handleCampaignRebateConfigurationSubmit = (rebate?: CampaignRebateConfiguration) => {
    if (rebate) {
      const isNew = !values.rebates.find((x) => x?.id === rebate?.id)
      const newRebates = isNew
        ? values.rebates.concat(rebate)
        : values.rebates.map((x) => (x.id === rebate.id ? rebate : x))
      setFieldValue('rebates', newRebates)
      setCampaignRebateConfigurationModal(initialModalDataState)
    }
  }

  const handleCampaignRebateConfigurationModalRemove = (rebate?: CampaignRebateConfiguration) => {
    if (rebate) {
      const newRebates = values.rebates.filter((x) => x.id !== rebate.id)
      setFieldValue('rebates', newRebates)
      setCampaignRebateConfigurationModal(initialModalDataState)
    }
  }

  const handleCampaignRebateConfigurationModalOpen = (rebate?: CampaignRebateConfiguration) => {
    setCampaignRebateConfigurationModal({
      data: rebate,
      visible: true,
    })
  }

  const handleCampaignRebateConfigurationModalClose = () => {
    setCampaignRebateConfigurationModal(initialModalDataState)
  }

  return {
    data: campaignRebateConfigurationModal,
    handleCampaignRebateConfigurationSubmit,
    handleCampaignRebateConfigurationModalRemove,
    handleCampaignRebateConfigurationModalOpen,
    handleCampaignRebateConfigurationModalClose,
  }
}

const useTypeIdChange = (props: CampaignDetailsFormProps) => {
  const { setFieldValue, onTypeIdChange, intervalTypes } = props

  const handleTypeIdChange = async (value: SelectValue) => {
    if (isNumeric(value)) {
      onTypeIdChange(value).then(() => {
        setFieldValue('typeId', value)
        setFieldValue('calculationTypeId', undefined)
      })
      if (value === MarketingCampaignType.MarginBonus) {
        const dailyIntervalType = intervalTypes.find((type) => type.name.toLowerCase() === 'daily')
        setFieldValue('intervalId', dailyIntervalType?.id)
        setFieldValue('intervalValue', MARGIN_BONUS_DEFAULT_INTERVAL_VALUE)
      }
    }
  }

  return { handleTypeIdChange }
}

type MarginBonusTierError = { minDeposit?: string; bonusAwardedPercentage?: string }
const MARGIN_BONUS_DEFAULT_INTERVAL_VALUE = 1

const CampaignDetailsFormUI: React.FC<CampaignDetailsFormProps> = (props) => {
  const {
    values,
    setFieldValue,
    errors,
    tradingAccountTypes = [],
    platformTypes = [],
    currencies = [],
    instruments = [],
    campaignTypes = [],
    campaignStatuses = [],
    campaignBusinessRuleTypes = [],
    calculationTypes = [],
    intervalTypes = [],
    tickmillCompanies = [],
    restrictions = [],
    marginBonusWithdrawRules,
  } = props
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [isEdit, setEdit] = useState<boolean>(false)
  const [isEquivalentInDefaultCurrency, setEquivalentInDefaultCurrency] = useState<boolean>(
    props.values.useCustomCurrencyAmount
  )
  const isMarginBonus = isMarginBonusType(values.typeId)
  const supportedPlatformTypes = useMemo(
    () => (isMarginBonus ? platformTypes.filter((pt) => isMT5Platform(pt.id)) : platformTypes),
    [isMarginBonus, platformTypes]
  )

  const { handleTypeIdChange } = useTypeIdChange(props)

  const currencyModal = useCurrencyModal(props)
  const campaignBusinessRuleModal = useCampaignBusinessRuleModal(props)
  const campaignRebateConfigurationModal = useCampaignRebateConfigurationModal(props)

  const useInstrumentSelection = (props: CampaignDetailsFormProps) => {
    const { setFieldValue, values } = props

    const [instrumentTypes, setInstrumentTypes] = useState<NameDto[]>()

    useEffect(() => {
      const getInstrumentTypes = async () => {
        try {
          const instrumentTypes = await tradingAccountsApiClient.getTradingAccountInstrumentTypes()
          setInstrumentTypes(instrumentTypes)
        } catch (error) {
          console.error('Error fetching instrument types:', error)
        }
      }
      getInstrumentTypes()
    }, [])

    const handleAssetClassSelect = async (typeId: number | number[]) => {
      try {
        const typeIds = Array.isArray(typeId) ? typeId : [typeId]

        const instruments = await tradingAccountsApiClient.getInstrumentsByTypes(typeIds)

        const newSymbols = instruments.map((instrument: InstrumentDto<string>) => instrument.symbol)

        const updatedSymbols = Array.from(new Set([...values.symbols, ...newSymbols]))
        setFieldValue('symbols', updatedSymbols)
      } catch (error) {
        console.error('Error fetching instruments by type:', error)
      }
    }

    const handleSelectAllInstruments = async () => {
      try {
        const instruments = await tradingAccountsApiClient.getInstruments()

        const allSymbols = instruments.map((instrument) => instrument.symbol)
        setFieldValue('symbols', allSymbols)
      } catch (error) {
        console.error('Error fetching all instruments:', error)
      }
    }

    return {
      instrumentTypes,
      handleAssetClassSelect,
      handleSelectAllInstruments,
    }
  }

  const { instrumentTypes, handleAssetClassSelect, handleSelectAllInstruments } =
    useInstrumentSelection(props)

  const campaignTypeOptions = useMemo(
    () => campaignTypes.map((campaignType) => ({ id: campaignType.id, name: campaignType.name })),
    [campaignTypes]
  )
  const calculationTypeOptions = useMemo(
    () =>
      calculationTypes.map((calculationType) => ({
        id: calculationType.id,
        name: calculationType.name,
      })),
    [calculationTypes]
  )
  const currencyOptions = useMemo(
    () => currencies.map((currency) => ({ id: currency.id, name: currency.id })),
    [currencies]
  )
  const statusOptions = useMemo(
    () => campaignStatuses.map((status) => ({ id: status.id, name: status.name })),
    [campaignStatuses]
  )
  const intervalOptions = useMemo(
    () => intervalTypes.map((intervalType) => ({ id: intervalType.id, name: intervalType.name })),
    [intervalTypes]
  )
  const platformOptions = useMemo(
    () =>
      supportedPlatformTypes.map((platformType) => ({
        id: platformType.id,
        name: platformType.name,
      })),
    [supportedPlatformTypes]
  )
  const companyOptions = useMemo(
    () => tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [tickmillCompanies]
  )
  const restrictionOptions = useMemo(
    () => restrictions.map((restriction) => ({ id: restriction.id, name: restriction.name })),
    [restrictions]
  )
  const marginBonusWithdrawRuleOptions = useMemo(
    () => marginBonusWithdrawRules.map((rule) => ({ id: rule.id, name: rule.name })),
    [marginBonusWithdrawRules]
  )
  const instrumentOptions = useMemo(
    () =>
      [...instruments]
        .sort((a, b) => a.symbol.localeCompare(b.symbol))
        .map((instrument) => ({ id: instrument.symbol, name: instrument.symbol })),
    [instruments]
  )
  const instrumentMenuItems = useMemo<SelectMenuItem[]>(
    () => [
      { type: 'label', key: 'quick-select-label', label: 'Quick Select' },
      {
        type: 'action',
        key: 'quick-select-all',
        label: 'All',
        onSelect: () => {
          void handleSelectAllInstruments()
        },
      },
      ...(instrumentTypes ?? []).map((type) => ({
        type: 'action' as const,
        key: `quick-select-${type.id}`,
        label: type.name,
        onSelect: () => {
          void handleAssetClassSelect(type.id)
        },
      })),
    ],
    [handleAssetClassSelect, handleSelectAllInstruments, instrumentTypes]
  )

  const handleSubmitForm = () => {
    setEdit(false)
    props.handleSubmit()
  }

  const handleCancel = () => {
    setEdit(false)
    setEquivalentInDefaultCurrency(props.values.useCustomCurrencyAmount)
    props.resetForm()
  }

  const getTierErrors = (idx: number) => {
    const tiers = errors.marginBonusConfiguration?.campaignMarginBonusTiers
    if (!tiers || typeof tiers === 'string') return undefined
    return (tiers as MarginBonusTierError[])[idx]
  }

  return (
    <React.Fragment>
      {currencyModal.isEditCurrencyModalOpen && (
        <Dialog
          open={currencyModal.isEditCurrencyModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              currencyModal.handleCurrencyModalClose()
            }
          }}
        >
          <CampaignDetailsCurrencyModal
            currencies={values.currencies}
            currenciesMinDeposit={values.currenciesMinDeposit}
            onChange={currencyModal.handleCurrency}
            onSubmit={currencyModal.handleSubmitCurrencies}
          />
        </Dialog>
      )}
      {campaignBusinessRuleModal.data && (
        <Dialog
          open={campaignBusinessRuleModal.data.visible}
          onOpenChange={(open) => {
            if (!open) {
              campaignBusinessRuleModal.handleCampaignBusinessRuleModalClose()
            }
          }}
        >
          <CampaignDetailsBusinessRuleModal
            data={campaignBusinessRuleModal.data.data}
            businessRules={values.businessRules}
            campaignBusinessRuleTypes={campaignBusinessRuleTypes}
            onSubmit={campaignBusinessRuleModal.handleCampaignBusinessRuleSubmit}
          />
        </Dialog>
      )}
      {campaignRebateConfigurationModal.data && (
        <Dialog
          open={campaignRebateConfigurationModal.data.visible}
          onOpenChange={(open) => {
            if (!open) {
              campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalClose()
            }
          }}
        >
          <CampaignRebateConfigurationModal
            data={campaignRebateConfigurationModal.data.data}
            rebates={values.rebates}
            calculationTypeId={values.calculationTypeId}
            tradingAccountTypes={tradingAccountTypes}
            calculationTypes={calculationTypes}
            onSubmit={campaignRebateConfigurationModal.handleCampaignRebateConfigurationSubmit}
          />
        </Dialog>
      )}
      <div className='px-4 md:px-6'>
        <Form>
          <DataGrid
            rightAddon={
              <ActionButtons
                {...props}
                isEdit={isEdit}
                setEdit={setEdit}
                onSubmit={handleSubmitForm}
                onCancel={handleCancel}
              />
            }
          >
            <DataColumn>
              <DataCard columns={2} title='Marketing Campaigns Options'>
                <FormField
                  name='name'
                  label='Campaign Name'
                  error={errors.name}
                  readOnly={!isEdit}
                />
                <FormField name='key' label='Campaign Key' error={errors.key} readOnly={!isEdit} />
                <FormField
                  name='campaignDescription'
                  label='Campaign Description'
                  readOnly={!isEdit}
                  error={errors.campaignDescription}
                />
                <FormSelectField
                  name='typeId'
                  label='Campaign Type'
                  error={errors.typeId}
                  value={values.typeId}
                  readOnly={!isEdit}
                  options={campaignTypeOptions}
                  onChange={(value) => {
                    void handleTypeIdChange(value as SelectValue)
                  }}
                />
                <FormSelectField
                  name='calculationTypeId'
                  label='Calculation Type'
                  value={values.calculationTypeId}
                  readOnly={!isEdit}
                  error={errors.calculationTypeId}
                  options={calculationTypeOptions}
                  onChange={(value) => setFieldValue('calculationTypeId', value)}
                />
                <FormDateField
                  name='from'
                  label='Start Date'
                  error={errors.from}
                  readOnly={!isEdit}
                  showTimeSelect
                />
                <FormDateField
                  name='to'
                  label='End Date'
                  error={errors.to}
                  readOnly={!isEdit}
                  showTimeSelect
                />
                <FormField
                  name='termsAndConditionsUrl'
                  label='Terms & Conditions URL'
                  error={errors.termsAndConditionsUrl}
                  readOnly={!isEdit}
                  link={values.termsAndConditionsUrl || undefined}
                />
                <FormSelectField
                  name='defaultCurrencyId'
                  label='Default Currency'
                  value={values.defaultCurrencyId}
                  error={errors.defaultCurrencyId}
                  readOnly={!isEdit}
                  options={currencyOptions}
                  onChange={(value) => setFieldValue('defaultCurrencyId', value)}
                />
                <SelectCurrencyField
                  currencies={values.currencies}
                  currenciesMinDeposit={values.currenciesMinDeposit}
                  error={typeof errors.currencies === 'string' ? errors.currencies : undefined}
                  readOnly={!isEdit}
                  options={currencyOptions}
                  onChange={(labeledValues, currenciesMinDeposit) => {
                    setFieldValue('currencies', labeledValues)
                    setFieldValue('currenciesMinDeposit', currenciesMinDeposit)
                  }}
                />
                <FormSelectField
                  name='statusId'
                  label='Status'
                  error={errors.statusId}
                  value={values.statusId}
                  readOnly={!isEdit}
                  options={statusOptions}
                  onChange={(value) => setFieldValue('statusId', Number(value))}
                />
                <FormSelectField
                  name='intervalId'
                  label='Interval Type'
                  value={values.intervalId}
                  readOnly={!isEdit}
                  disabled={isMarginBonus}
                  error={errors.intervalId}
                  options={intervalOptions}
                  onChange={(value) => setFieldValue('intervalId', Number(value))}
                />
                <FormField
                  name='intervalValue'
                  label='Interval Value'
                  type='number'
                  readOnly={!isEdit}
                  disabled={isMarginBonus}
                  error={errors.intervalValue}
                />
                <FormMultiSelectField
                  name='symbols'
                  label='Applicable Symbols'
                  value={values.symbols}
                  options={instrumentOptions}
                  error={errors.symbols?.toString()}
                  menuItems={instrumentMenuItems}
                  readOnly={!isEdit}
                  allowClear
                  showSearch
                  onChange={(value) =>
                    setFieldValue(
                      'symbols',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
                    )
                  }
                />
                <FormMultiSelectField
                  name='platformTypes'
                  label='Available Platform Types'
                  value={values.platformTypes}
                  error={errors.platformTypes?.toString()}
                  readOnly={!isEdit}
                  options={platformOptions}
                  onChange={(value) =>
                    setFieldValue(
                      'platformTypes',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
                <FormMultiSelectField
                  name='tickmillCompanies'
                  label='Tickmill Companies'
                  value={values.tickmillCompanies}
                  placeholder='Companies'
                  readOnly={!isEdit}
                  error={errors.tickmillCompanies?.toString()}
                  options={companyOptions}
                  onChange={(value) =>
                    setFieldValue(
                      'tickmillCompanies',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
                <FormCheckboxField
                  name='internalOnly'
                  label='Internal Only'
                  value={values.internalOnly}
                  readOnly={!isEdit}
                />
              </DataCard>
              <DataCard title='Campaign Restrictions'>
                <FormMultiSelectField
                  error={errors.restrictions?.toString()}
                  name='restrictions'
                  label='Restrictions'
                  value={values.restrictions}
                  readOnly={!isEdit}
                  options={restrictionOptions}
                  onChange={(value) =>
                    setFieldValue(
                      'restrictions',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
              </DataCard>
              <DataCard title='Campaign Business Rules'>
                <CampaignDetailsBusinessRulesTable
                  businessRules={values.businessRules}
                  isEdit={isEdit}
                  onAdd={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                  onEdit={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                  onRemove={campaignBusinessRuleModal.handleCampaignBusinessRuleModalRemove}
                />
              </DataCard>
            </DataColumn>

            <DataColumn>
              {isCampaignOptions(props) && (
                <DataCard columns={2} title='Campaign Options'>
                  <FormField
                    name='minDeposit'
                    label='Minimum Deposit'
                    type='number'
                    readOnly={!isEdit}
                    error={errors.minDeposit}
                    disabled={isEquivalentInDefaultCurrency}
                  />
                  <FormDateField
                    name='enrollmentTo'
                    label='Enrollment To Date'
                    readOnly={!isEdit}
                    error={errors.enrollmentTo}
                    showTimeSelect
                  />
                  <FormField
                    name='disqualificationEmailTemplate'
                    label='Disqualification Template'
                    readOnly={!isEdit}
                  />
                  <FormCheckboxField
                    name='useCustomCurrencyAmount'
                    label='Use Custom Currency Amount'
                    value={values.useCustomCurrencyAmount}
                    readOnly={!isEdit}
                    onChange={(checked) => {
                      setFieldValue('useCustomCurrencyAmount', checked)
                      setEquivalentInDefaultCurrency(checked)
                    }}
                  />
                  {isEquivalentInDefaultCurrency && (
                    <CampaignDetailsCurrencyTable
                      currencies={values.currencies}
                      currenciesMinDeposit={values.currenciesMinDeposit}
                      isEdit={isEdit}
                      onCurrencyModalOpen={currencyModal.handleCurrencyModalOpen}
                    />
                  )}
                </DataCard>
              )}
              {isCampaignRebateOption(props) && (
                <DataCard title='Rebate Options'>
                  <FormField
                    name='maxPayout'
                    label='Maximum Payout'
                    type='number'
                    error={errors.maxPayout}
                    readOnly={!isEdit}
                  />
                  <SwitchField
                    label='Issue Payouts'
                    value={values.issuePayouts}
                    onChange={(value) => setFieldValue('issuePayouts', value)}
                    error={errors.issuePayouts}
                    readOnly={!isEdit}
                  />
                  {values.issuePayouts && (
                    <>
                      <SwitchField
                        label='Autoapprove Payouts'
                        value={values.approvePayouts}
                        onChange={(value) => setFieldValue('approvePayouts', value)}
                        error={errors.approvePayouts}
                        readOnly={!isEdit}
                      />
                      <FormSelectField
                        name='payoutIntervalId'
                        error={errors.payoutIntervalId}
                        label='Payout Period'
                        value={values.payoutIntervalId}
                        readOnly={!isEdit}
                        options={intervalOptions}
                        onChange={(value) => setFieldValue('payoutIntervalId', Number(value))}
                      />
                    </>
                  )}

                  {!!values.calculationTypeId && (
                    <CampaignRebateConfigurationTable
                      calculationTypeId={values.calculationTypeId}
                      tradingAccountTypes={tradingAccountTypes}
                      rebates={values.rebates}
                      isEdit={isEdit}
                      onAdd={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalOpen
                      }
                      onEdit={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalOpen
                      }
                      onRemove={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalRemove
                      }
                    />
                  )}
                </DataCard>
              )}
              {isLuckyDraw(props) && (
                <DataCard title={`${getMarketingCampaignName(values.typeId)} Options`}>
                  <FormField
                    name='minDeposit'
                    label='Minimum Deposit'
                    type='number'
                    readOnly={!isEdit}
                    error={errors.minDeposit}
                  />
                  <FormField
                    name='maxPayout'
                    label='Maximum Payout'
                    type='number'
                    error={errors.maxPayout}
                    readOnly={!isEdit}
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxDailyUsableDrawChances'
                    label='Max Daily Usable Draw Chances'
                    type='number'
                    readOnly={!isEdit}
                    error={errors.luckyDrawConfiguration?.maxDailyUsableDrawChances}
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.maxDailyGeneratedDrawChances}
                    name='luckyDrawConfiguration.maxDailyGeneratedDrawChances'
                    label='Max Daily Generated Draw Chances'
                    type='number'
                    readOnly={!isEdit}
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.maxDrawChancesSingleTransaction}
                    name='luckyDrawConfiguration.maxDrawChancesSingleTransaction'
                    label='Max Draw Chances Single Transaction'
                    type='number'
                    readOnly={!isEdit}
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.maxWinChancesPerClient}
                    name='luckyDrawConfiguration.maxWinChancesPerClient'
                    label='Max Win Chances Per Client'
                    type='number'
                    readOnly={!isEdit}
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed'
                    label='Max Weekly Maximum Rewards All'
                    type='number'
                    readOnly={!isEdit}
                    error={errors.luckyDrawConfiguration?.maxWeeklyMaximumRewardsAllowed}
                  />
                  <FormField
                    name='luckyDrawConfiguration.minValueAllowance'
                    label='Min Value Allowance'
                    type='number'
                    readOnly={!isEdit}
                    error={errors.luckyDrawConfiguration?.minValueAllowance}
                  />
                  <FormSelectField
                    name='luckyDrawConfiguration.expirationIntervalId'
                    label='Expiration Interval Type'
                    value={values.luckyDrawConfiguration.expirationIntervalId}
                    error={errors.luckyDrawConfiguration?.expirationIntervalId}
                    readOnly={!isEdit}
                    options={intervalOptions}
                    onChange={(value) =>
                      setFieldValue(
                        'luckyDrawConfiguration.expirationIntervalId',
                        value ? Number(value) : undefined
                      )
                    }
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.expirationIntervalValue}
                    name='luckyDrawConfiguration.expirationIntervalValue'
                    label='Expiration Interval Value'
                    type='number'
                    readOnly={!isEdit}
                  />
                  {isEquivalentInDefaultCurrency && (
                    <CampaignDetailsCurrencyTable
                      currencies={values.currencies}
                      currenciesMinDeposit={values.currenciesMinDeposit}
                      isEdit={isEdit}
                      onCurrencyModalOpen={currencyModal.handleCurrencyModalOpen}
                    />
                  )}
                  <FormField
                    name='luckyDrawConfiguration.maxPayoutDivisionPerDay'
                    label='Max Payout Division Per Day'
                    type='number'
                    readOnly={!isEdit}
                  />
                </DataCard>
              )}
              {isMarginBonus && (
                <DataCard title={`${getMarketingCampaignName(values.typeId)} Options`}>
                  <div className='grid grid-cols-[1fr_2fr_2fr] gap-4 text-sm font-medium'>
                    <div>Bonus Tiers</div>
                    <div>Condition (min Deposit)</div>
                    <div>Bonus Awarded</div>
                  </div>
                  {values.marginBonusConfiguration.campaignMarginBonusTiers.map((_, idx) => {
                    const tierErrors = getTierErrors(idx)
                    return (
                      <div
                        key={`mb-tier-${idx}`}
                        className='grid grid-cols-[1fr_2fr_2fr] gap-4 items-start'
                      >
                        <div />
                        <FormField
                          error={tierErrors?.minDeposit}
                          name={`marginBonusConfiguration.campaignMarginBonusTiers.${idx}.minDeposit`}
                          label='Min Deposit'
                          type='number'
                          readOnly={!isEdit}
                        />
                        <FormField
                          label='Bonus Awarded'
                          error={tierErrors?.bonusAwardedPercentage}
                          name={`marginBonusConfiguration.campaignMarginBonusTiers.${idx}.bonusAwardedPercentage`}
                          type='number'
                          min='0'
                          readOnly={!isEdit}
                          value={
                            values.marginBonusConfiguration.campaignMarginBonusTiers[idx]
                              ?.bonusAwardedPercentage ?? ''
                          }
                          onChange={(event) =>
                            setFieldValue(
                              `marginBonusConfiguration.campaignMarginBonusTiers.${idx}.bonusAwardedPercentage`,
                              event.target.value === '' ? undefined : parseFloat(event.target.value)
                            )
                          }
                          rightAddon={
                            <Button
                              disabled={!isEdit}
                              key={`remove-tier-${idx}`}
                              variant='outline'
                              size='icon-xs'
                              onClick={() => {
                                const next = [
                                  ...values.marginBonusConfiguration.campaignMarginBonusTiers,
                                ]
                                next.splice(idx, 1)
                                setFieldValue(
                                  'marginBonusConfiguration.campaignMarginBonusTiers',
                                  next
                                )
                              }}
                            >
                              X
                            </Button>
                          }
                        />
                      </div>
                    )
                  })}
                  {errors.marginBonusConfiguration?.campaignMarginBonusTiers &&
                    typeof errors.marginBonusConfiguration.campaignMarginBonusTiers ===
                      'string' && (
                      <FormErrorText
                        message={errors.marginBonusConfiguration.campaignMarginBonusTiers}
                      />
                    )}
                  <Button
                    disabled={!isEdit}
                    key='add-tier'
                    type='button'
                    variant='outline'
                    onClick={() => {
                      setFieldValue('marginBonusConfiguration.campaignMarginBonusTiers', [
                        ...values.marginBonusConfiguration.campaignMarginBonusTiers,
                        { minDeposit: 0, bonusAwardedPercentage: 0 },
                      ])
                    }}
                  >
                    +
                  </Button>

                  <FormField
                    error={errors.marginBonusConfiguration?.maximumBonus}
                    name='marginBonusConfiguration.maximumBonus'
                    label='Maximum Bonus'
                    type='number'
                    readOnly={!isEdit}
                  />
                  <FormSelectField
                    name='marginBonusConfiguration.campaignMarginBonusWithdrawRuleId'
                    label='Withdrawal Rule'
                    value={
                      values.marginBonusConfiguration.campaignMarginBonusWithdrawRuleId ?? undefined
                    }
                    readOnly={!isEdit}
                    options={marginBonusWithdrawRuleOptions}
                    onChange={(value) =>
                      setFieldValue(
                        'marginBonusConfiguration.campaignMarginBonusWithdrawRuleId',
                        value ? Number(value) : undefined
                      )
                    }
                  />
                  <FormCheckboxField
                    name='marginBonusConfiguration.removeCreditOnStopOut'
                    label='Remove Credit on Stop Out'
                    value={values.marginBonusConfiguration.removeCreditOnStopOut}
                    readOnly={!isEdit}
                  />
                  <FormField
                    name='marginBonusConfiguration.validForDays'
                    label='Bonus Valid for Days'
                    type='number'
                    readOnly={!isEdit}
                  />
                  <FormDateField
                    error={errors.enrollmentTo}
                    name='enrollmentTo'
                    label='Enrollment To Date'
                    readOnly={!isEdit}
                    showTimeSelect
                  />
                </DataCard>
              )}
            </DataColumn>
          </DataGrid>
        </Form>
      </div>
    </React.Fragment>
  )
}

interface ActionButtonsProps extends Omit<CampaignDetailsFormProps, 'onSubmit'> {
  isEdit: boolean
  setEdit(value: boolean): void
  onSubmit(): void
  onCancel(): void
}

const ActionButtons: React.FC<ActionButtonsProps> = (props) => {
  const {
    values,
    permissions,
    isValid,
    isEdit,
    releaseCampaignRequest,
    setAuditLogModal,
    setEdit,
    onSubmit,
    onCancel,
  } = props

  const handleEditOpen = () => {
    setEdit(true)
  }

  const handleAuditLogModalOpen = () => {
    setAuditLogModal(true)
  }

  return (
    <ButtonGroup>
      {!isEdit ? (
        <Button
          disabled={!hasPermission(permissions, PermissionsEnum.CanEditMarketingCampaigns)}
          type='button'
          onClick={handleEditOpen}
        >
          Edit
        </Button>
      ) : (
        <React.Fragment>
          <Button className='is-danger' type='button' variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button key='button' type='submit' onClick={onSubmit} disabled={!isValid}>
            Save
          </Button>
        </React.Fragment>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type='button'
              aria-label='More options'
              variant='outline'
              size='icon'
              data-test='more-button'
            />
          }
        >
          <ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <CampaignActionsMenuContent
            values={values}
            onAuditLogModalOpen={handleAuditLogModalOpen}
            onReleaseAccounts={releaseCampaignRequest}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  )
}

interface CampaignActionsMenuContentProps {
  values: CampaignDetailsFormValues
  onReleaseAccounts(): void
  onAuditLogModalOpen(): void
}

const CampaignActionsMenuContent: React.FC<CampaignActionsMenuContentProps> = (props) => {
  const { values, onAuditLogModalOpen, onReleaseAccounts } = props

  const marketingCampaignTypes = [
    MarketingCampaignType.ClientRebate,
    MarketingCampaignType.ClientContest,
  ]

  return (
    <>
      <DropdownMenuItem onClick={onAuditLogModalOpen} data-test='audit-log-menu-button'>
        <AuditLogIcon style={{ width: 12, height: 12, cursor: 'pointer' }} /> Audit Log
      </DropdownMenuItem>
      {values?.typeId != null && marketingCampaignTypes.includes(toTypeId(values.typeId)) && (
        <DropdownMenuItem onClick={onReleaseAccounts} data-test='release-accounts-menu-button'>
          Release Accounts
        </DropdownMenuItem>
      )}
    </>
  )
}

const SwitchField: React.FC<{
  label: React.ReactNode
  value: boolean
  onChange(value: boolean): void
  error?: React.ReactNode
  readOnly?: boolean
  disabled?: boolean
}> = ({ label, value, onChange, error, readOnly, disabled }) => (
  <FormFieldLayout label={label} error={error?.toString()}>
    <CheckboxSwitch
      name={typeof label === 'string' ? label : 'toggle'}
      initialValue={value}
      readOnly={readOnly}
      disabled={disabled}
      onChange={onChange}
    />
  </FormFieldLayout>
)

const SelectCurrencyField: React.FC<{
  currencies: LabeledValue[]
  currenciesMinDeposit: Record<CurrencyType, number>
  error?: React.ReactNode
  readOnly?: boolean
  disabled?: boolean
  options: { id: CurrencyType; name: string }[]
  onChange(labeledValues: LabeledValue[], currenciesMinDeposit: Record<CurrencyType, number>): void
}> = ({ currencies, currenciesMinDeposit, error, readOnly, disabled, options, onChange }) => {
  const handleChange = (value: string[] | string | number | number[] | undefined) => {
    if (readOnly || disabled) {
      return
    }

    const selectedValues = Array.isArray(value) ? value : value != null ? [value] : []
    const labeledValues: LabeledValue[] = selectedValues
      .map((currencyId) => options.find((option) => option.id === currencyId))
      .filter((option): option is { id: CurrencyType; name: string } => Boolean(option))
      .map((option) => ({
        label: option.name,
        value: option.id,
      }))

    const currentCurrencyIds = new Set(currencies.map((currency) => currency.value as CurrencyType))
    const nextCurrenciesMinDeposit = labeledValues.reduce(
      (acc, currency) => {
        const currencyId = currency.value as CurrencyType
        acc[currencyId] = currentCurrencyIds.has(currencyId)
          ? currenciesMinDeposit[currencyId] || 0
          : 0
        return acc
      },
      {} as Record<CurrencyType, number>
    )

    onChange(labeledValues, nextCurrenciesMinDeposit)
  }

  return (
    <FormMultiSelectField
      error={error?.toString()}
      options={options}
      label='Applicable Currencies'
      name='currencies'
      readOnly={readOnly}
      disabled={disabled}
      value={currencies.map((currency) => currency.value)}
      onChange={handleChange}
    />
  )
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  campaignTypeId: number
  permissions: PermissionDto[]
  restrictions: RestrictionDto[]
  dateFormatType?: string

  tradingAccountTypes: NameDto[]
  instruments: InstrumentDto[]
  intervalTypes: NameDto[]
  currencies: WalletCurrencyDto[]
  campaignStatuses: NameDto[]
  campaignBusinessRuleTypes: NameDto[]
  campaignTypes: CampaignTypeDto[]
  calculationTypes: NameDto[]
  platformTypes: PlatformTypeDto[]
  tickmillCompanies: TickmillCompanyDto[]

  releaseCampaignRequest(): Promise<void>
  setAuditLogModal(value: boolean): void

  onTypeIdChange(typeId: number): Promise<void>
  onSubmit(values: FormSubmitValues<AddUpdateMarketingCampaign>): Promise<void>
  marginBonusWithdrawRules: NameDto[]
}

const dataToSubmit = (
  props: OuterProps,
  values: CampaignDetailsFormValues
): FormSubmitValues<AddUpdateMarketingCampaign> => {
  const { data, instruments, tickmillCompanies } = props

  return {
    key: values.key,
    name: values.name,
    typeId: Number.parseInt(values.typeId.toString(), 10),
    calculationTypeId: values.calculationTypeId
      ? Number.parseInt(values.calculationTypeId.toString(), 10)
      : null,
    from: values.from ? zonedTimeToUtc(new Date(values.from)).toISOString() : null,
    to: values.to ? zonedTimeToUtc(new Date(values.to)).toISOString() : null,
    enrollmentTo: values.enrollmentTo
      ? zonedTimeToUtc(new Date(values.enrollmentTo)).toISOString()
      : null,
    disqualificationEmailTemplate: values.disqualificationEmailTemplate || null,
    statusId: values.statusId,
    termsAndConditionsUrl: values.termsAndConditionsUrl || null,
    minDeposit: values.minDeposit || null,
    maxPayout: values.maxPayout || null,
    defaultCurrencyId: values.defaultCurrencyId,
    intervalId: values.intervalId,
    intervalValue: values.intervalValue,
    calculatePayouts: values.calculatePayouts,
    issuePayouts: values.issuePayouts,
    payoutIntervalId: values.payoutIntervalId,
    currencies: Object.keys(values.currenciesMinDeposit).map((currencyId) => ({
      currencyId,
      minDeposit: values.currenciesMinDeposit[currencyId as CurrencyType],
    })),
    countryOverrides:
      data?.countryOverrides.map((x) => ({
        country: x.country,
        tickmillCompany: x.tickmillCompany,
        minAmount: x.minAmount,
        enabled: x.enabled,
      })) || [],
    symbols: (values.symbols || [])
      .map((symbol) => {
        const instrument = instruments.find((x) => x.symbol === symbol)
        return instrument?.symbol
      })
      .filter((x): x is string => !!x),
    rebates:
      values.rebates.map((x) => ({
        ...x,
        campaignCalculationTypeId: x.campaignCalculationTypeId.id,
      })) || [],
    tickmillCompanies: (values.tickmillCompanies || [])
      .map((companyId) => {
        const tickmillCompany = tickmillCompanies.find((x) => x.id === companyId)
        return tickmillCompany?.id
      })
      .filter((x): x is number => !!x)
      .map((x) => Number.parseInt(x.toString(), 10))
      .filter((x): x is number => !!x),
    platformTypes: values.platformTypes
      .filter((x): x is number => !!x)
      .map((x) => Number.parseInt(x.toString(), 10)),
    approvePayouts: values.approvePayouts || false,
    restrictions: values.restrictions,
    businessRules: values.businessRules.map((x) => ({
      businessRuleId: x.rule.id,
      businessRuleValue: x.value,
    })),
    luckyDrawConfiguration: {
      expirationIntervalId: values.luckyDrawConfiguration.expirationIntervalId,
      expirationIntervalValue: values.luckyDrawConfiguration.expirationIntervalValue,
      minValueAllowance: values.luckyDrawConfiguration.minValueAllowance,
      maxDailyUsableDrawChances: values.luckyDrawConfiguration.maxDailyUsableDrawChances ?? null,
      maxDailyGeneratedDrawChances:
        values.luckyDrawConfiguration.maxDailyGeneratedDrawChances ?? null,
      maxDrawChancesSingleTransaction:
        values.luckyDrawConfiguration.maxDrawChancesSingleTransaction ?? null,
      maxWinChancesPerClient: values.luckyDrawConfiguration.maxWinChancesPerClient ?? null,
      maxWeeklyMaximumRewardsAllowed:
        values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed ?? null,
      maxPayoutDivisionPerDay: values.luckyDrawConfiguration.maxPayoutDivisionPerDay,
    },
    marginBonusConfiguration: isMarginBonusType(values.typeId)
      ? {
          campaignMarginBonusTiers: values.marginBonusConfiguration.campaignMarginBonusTiers,
          maximumBonus: values.marginBonusConfiguration.maximumBonus,
          campaignMarginBonusWithdrawRuleId:
            values.marginBonusConfiguration.campaignMarginBonusWithdrawRuleId,
          removeCreditOnStopOut: !!values.marginBonusConfiguration.removeCreditOnStopOut,
          validForDays: values.marginBonusConfiguration.validForDays ?? null,
        }
      : null,
    useCustomCurrencyAmount: values.useCustomCurrencyAmount,
    campaignDescription: values.campaignDescription,
    internalOnly: values.internalOnly,
  }
}

const initialState = (props: OuterProps) => {
  const { data, currencies, campaignTypeId } = props
  return {
    campaignDescription: data.campaignDescription || '',
    id: data.id,
    name: data.name || '',
    key: data.key || '',
    typeId: campaignTypeId,
    calculationTypeId: data.calculationType.id || undefined,
    from: utcToZonedTime(data.from),
    to: utcToZonedTime(data.to),
    enrollmentTo: data.enrollmentTo ? utcToZonedTime(data.enrollmentTo) : null,
    disqualificationEmailTemplate: data.disqualificationEmailTemplate || null,
    termsAndConditionsUrl: data.termsAndConditionsUrl || '',
    defaultCurrencyId: data.defaultCurrency?.id || 'USD',
    statusId: data.status?.id || 0,
    intervalId: data.interval?.id || 0,
    platformTypes: (data.platformTypes || []).map((x) => x.id),
    currencies: (data.currencies || []).map((c) => {
      const currency = currencies.find((x) => x.id === c.currencyId)
      return {
        label: currency?.id ?? c.currencyId,
        value: currency?.id ?? c.currencyId ?? 0,
        id: currency?.id ?? c.currencyId,
      }
    }),
    currenciesMinDeposit: data.currencies.reduce<Record<CurrencyType, number>>(
      (acc, curr) => {
        acc[curr.currencyId] = curr.minDeposit || 0
        return acc
      },
      {} as Record<CurrencyType, number>
    ),
    symbols: (data.symbols || []).map((symbol) => {
      return symbol
    }),
    rebates:
      data.rebates.map((x) => ({
        id: uuid(),
        ...x,
      })) || [],
    tickmillCompanies: (data.tickmillCompanies || []).map((x) => {
      return x.id
    }),
    restrictions: data.restrictions.map((x) => x.id) || [],
    businessRules: (data.businessRules || []).map((x) => ({
      id: uuid(),
      type: { id: x.businessRule.businessRuleType.id, name: x.businessRule.businessRuleType.name },
      rule: { id: x.businessRule.id, name: x.businessRule.name },
      value: x.businessRuleValue || null,
    })),
    minDeposit: data?.minDeposit || undefined,
    maxPayout: data?.maxPayout || undefined,
    intervalValue: data.intervalValue,
    payoutIntervalId: data.payoutInterval?.id || 0,
    issuePayouts: data.issuePayouts || false,
    calculatePayouts: data.calculatePayouts || false,
    approvePayouts: data.approvePayouts || false,
    luckyDrawConfiguration: {
      minValueAllowance: data.luckyDrawConfiguration?.minValueAllowance ?? undefined,
      expirationIntervalId: data.luckyDrawConfiguration?.expirationInterval?.id ?? undefined,
      expirationIntervalValue: data.luckyDrawConfiguration?.expirationIntervalValue ?? undefined,
      maxDailyUsableDrawChances:
        data.luckyDrawConfiguration?.maxDailyUsableDrawChances ?? undefined,
      maxDailyGeneratedDrawChances:
        data.luckyDrawConfiguration?.maxDailyGeneratedDrawChances ?? undefined,
      maxDrawChancesSingleTransaction:
        data.luckyDrawConfiguration?.maxDrawChancesSingleTransaction ?? undefined,
      maxWinChancesPerClient: data.luckyDrawConfiguration?.maxWinChancesPerClient ?? undefined,
      maxWeeklyMaximumRewardsAllowed:
        data.luckyDrawConfiguration?.maxWeeklyMaximumRewardsAllowed ?? undefined,
      campaignTimeZoneId: data.luckyDrawConfiguration?.campaignTimeZoneId || '',
      maxPayoutDivisionPerDay: data.luckyDrawConfiguration?.maxPayoutDivisionPerDay,
    },
    marginBonusConfiguration: {
      campaignMarginBonusTiers: data.marginBonusConfiguration?.campaignMarginBonusTiers ?? [],
      maximumBonus: data.marginBonusConfiguration?.maximumBonus ?? undefined,
      campaignMarginBonusWithdrawRuleId:
        data.marginBonusConfiguration?.campaignMarginBonusWithdrawRule?.id ?? undefined,
      removeCreditOnStopOut: data.marginBonusConfiguration?.removeCreditOnStopOut ?? false,
      validForDays: data.marginBonusConfiguration?.validForDays ?? undefined,
    },
    useCustomCurrencyAmount: data.useCustomCurrencyAmount,
    internalOnly: data.internalOnly ?? false,
  }
}

export const CampaignDetailsForm = withCustomFormik<OuterProps, CampaignDetailsFormValues>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(dataToSubmit(props, values))
  },
  validate: (values, props) => {
    const { campaignTypes = [], campaignStatuses = [], intervalTypes = [] } = props
    const errors: FormikErrors<CampaignDetailsFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }
    if (!values.key) {
      errors.key = 'Required'
    }
    if (isEmpty(values.currencies)) {
      errors.currencies = 'Required'
    }
    if (
      values.typeId &&
      !campaignTypes.map((x) => x.id).includes(Number.parseInt(values.typeId.toString(), 10))
    ) {
      errors.typeId = 'Required'
    }
    if (!values.calculationTypeId) {
      errors.calculationTypeId = 'Required'
    }

    if (!values.from) {
      errors.from = 'Required'
    } else if (!values.to) {
      errors.to = 'Required'
    } else {
      const fromDate = new Date(values.from)
      const toDate = new Date(values.to)
      if (fromDate > toDate) {
        errors.from = 'Start date must be earlier than end date'
      }
    }

    if (!values.defaultCurrencyId) {
      errors.defaultCurrencyId = 'Required'
    }
    if (
      !campaignStatuses.map((x) => x.id).includes(Number.parseInt(values.statusId.toString(), 10))
    ) {
      errors.statusId = 'Required'
    }
    if (
      !intervalTypes.map((x) => x.id).includes(Number.parseInt(values.intervalId.toString(), 10))
    ) {
      errors.intervalId = 'Required'
    }
    if (values.intervalValue < 0) {
      errors.intervalValue = 'Required'
    }
    if (values.symbols.length <= 0) {
      errors.symbols = 'Required'
    }

    if (values.tickmillCompanies.length <= 0) {
      errors.tickmillCompanies = 'Required'
    }

    if (!values.termsAndConditionsUrl) {
      errors.termsAndConditionsUrl = 'Invalid URL. Please start with https://.'
    }
    if (!!values.termsAndConditionsUrl && !UrlPattern.test(values.termsAndConditionsUrl)) {
      errors.termsAndConditionsUrl = 'Invalid URL. Please start with https://.'
    }

    // Payout Options
    if (isCampaignOptions({ values })) {
      if (values.enrollmentTo && values.from && values.to) {
        const from = new Date(values.from)
        const to = new Date(values.to)
        const enrollmentTo = new Date(values.enrollmentTo)

        if (!(enrollmentTo >= from) || !(enrollmentTo <= to)) {
          errors.enrollmentTo = 'Enrollment date is outside the valid range'
        }
      }
      if (!values.useCustomCurrencyAmount && (!values.minDeposit || values.minDeposit <= 0)) {
        errors.minDeposit = `Invalid min deposit: ${values.minDeposit}`
      }

      if (isCampaignRebateOption({ values })) {
        if (!isMaxPayoutValid(values)) {
          errors.maxPayout = `Invalid max payout: ${values.maxPayout}`
        }
      }
    }

    // LuckyDraw Options
    if (isLuckyDraw({ values })) {
      if (!isLuckyDrawValid(values)) {
        errors.luckyDrawConfiguration = {}
        if (!isLuckyDrawExpirationIntervalIdValid(values)) {
          errors.luckyDrawConfiguration.expirationIntervalId = `Invalid expiration Interval Id: ${
            values.luckyDrawConfiguration.expirationIntervalId || ''
          }`
        }
        if (!isLuckyDrawExpirationIntervalValueValid(values)) {
          errors.luckyDrawConfiguration.expirationIntervalValue = `Invalid expiration Interval Value: ${
            values.luckyDrawConfiguration.expirationIntervalValue || ''
          }`
        }
        if (!isLuckyDrawMinValueAllowanceValid(values)) {
          errors.luckyDrawConfiguration.minValueAllowance = `Invalid min Value Allowance: ${
            values.luckyDrawConfiguration.minValueAllowance || ''
          }`
        }
        if (!isLuckyDrawMaxDailyUsableDrawChancesValid(values)) {
          errors.luckyDrawConfiguration.maxDailyUsableDrawChances = `Invalid max Daily Usable Draw Chances: ${
            values.luckyDrawConfiguration.maxDailyUsableDrawChances || ''
          }`
        }
        if (!isLuckyDrawMaxDailyGeneratedDrawChancesValid(values)) {
          errors.luckyDrawConfiguration.maxDailyGeneratedDrawChances = `Invalid max Daily Generated Draw Chances: ${
            values.luckyDrawConfiguration.maxDailyGeneratedDrawChances || ''
          }`
        }
        if (!isLuckyDrawMaxDrawChancesSingleTransactionValid(values)) {
          errors.luckyDrawConfiguration.maxDrawChancesSingleTransaction = `Invalid max Draw Chances Single Transaction: ${
            values.luckyDrawConfiguration.maxDrawChancesSingleTransaction || ''
          }`
        }
        if (!isLuckyDrawMaxWinChancesPerClientValid(values)) {
          errors.luckyDrawConfiguration.maxWinChancesPerClient = `Invalid max Win Chances Per Client: ${
            values.luckyDrawConfiguration.maxWinChancesPerClient || ''
          }`
        }
        if (!isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid(values)) {
          errors.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed = `Invalid max Weekly Maximum Rewards Allowed: ${
            values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed || ''
          }`
        }
      }

      if (!isMaxPayoutValid(values)) {
        errors.maxPayout = `Invalid max payout: ${values.maxPayout || ''}`
      }
    }

    if (isMarginBonusType(values.typeId)) {
      const mbErrors: FormikErrors<CampaignDetailsFormValues['marginBonusConfiguration']> = {}

      const { campaignMarginBonusWithdrawRuleId, maximumBonus, campaignMarginBonusTiers } =
        values.marginBonusConfiguration

      if (campaignMarginBonusWithdrawRuleId == null) {
        mbErrors.campaignMarginBonusWithdrawRuleId = 'Required'
      }

      const enrollmentTo = values.enrollmentTo

      if (enrollmentTo == null) {
        errors.enrollmentTo = 'Required'
      } else {
        const fromDate = values.from
        const toDate = values.to

        const isInvalid = !fromDate || !toDate || enrollmentTo < fromDate || enrollmentTo > toDate

        if (isInvalid) {
          errors.enrollmentTo = 'Enrollment date must be between start date and end date'
        }
      }

      if (maximumBonus == null || Number.isNaN(maximumBonus)) {
        mbErrors.maximumBonus = 'Required'
      } else if (maximumBonus <= 0) {
        mbErrors.maximumBonus = 'Maximum bonus must be greater than zero'
      }

      if (!campaignMarginBonusTiers.length) {
        mbErrors.campaignMarginBonusTiers = 'Required'
      } else {
        const tierErrors: FormikErrors<
          CampaignDetailsFormValues['marginBonusConfiguration']['campaignMarginBonusTiers'][number]
        >[] = campaignMarginBonusTiers.map((tier) => {
          const tErrors: FormikErrors<
            CampaignDetailsFormValues['marginBonusConfiguration']['campaignMarginBonusTiers'][number]
          > = {}

          if (Number.isNaN(tier.minDeposit) || tier.minDeposit <= 0) {
            tErrors.minDeposit = 'Minimum deposit must be greater than 0'
          }

          if (Number.isNaN(tier.bonusAwardedPercentage) || tier.bonusAwardedPercentage <= 0) {
            tErrors.bonusAwardedPercentage = 'Bonus awarded percentage must be greater than 0'
          } else if (tier.bonusAwardedPercentage > 100) {
            tErrors.bonusAwardedPercentage = 'Bonus awarded percentage must be less or equal to 100'
          }

          return tErrors
        })

        if (tierErrors.some((tierError) => Object.keys(tierError).length > 0)) {
          mbErrors.campaignMarginBonusTiers = tierErrors
        }
      }

      if (Object.keys(mbErrors).length > 0) {
        errors.marginBonusConfiguration = mbErrors
      }
    }

    return errors
  },
  enableReinitialize: false,
  resetOnError: true,
})(CampaignDetailsFormUI)

const isLuckyDrawValid = (values: CampaignDetailsFormValues) => {
  return (
    isLuckyDrawExpirationIntervalIdValid(values) &&
    isLuckyDrawExpirationIntervalValueValid(values) &&
    isLuckyDrawMinValueAllowanceValid(values) &&
    isLuckyDrawMaxDailyUsableDrawChancesValid(values) &&
    isLuckyDrawMaxDailyGeneratedDrawChancesValid(values) &&
    isLuckyDrawMaxDrawChancesSingleTransactionValid(values) &&
    isLuckyDrawMaxWinChancesPerClientValid(values) &&
    isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid(values)
  )
}

const isLuckyDrawExpirationIntervalIdValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.expirationIntervalId &&
    values.luckyDrawConfiguration.expirationIntervalId >= 0
  )
}

const isLuckyDrawExpirationIntervalValueValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.expirationIntervalValue &&
    values.luckyDrawConfiguration.expirationIntervalValue > 0
  )
}

const isLuckyDrawMinValueAllowanceValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.minValueAllowance &&
    values.luckyDrawConfiguration.minValueAllowance > 0
  )
}

const isLuckyDrawMaxDailyUsableDrawChancesValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDailyUsableDrawChances &&
    values.luckyDrawConfiguration.maxDailyUsableDrawChances > 0
  )
}

const isLuckyDrawMaxDailyGeneratedDrawChancesValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDailyGeneratedDrawChances &&
    values.luckyDrawConfiguration.maxDailyGeneratedDrawChances > 0
  )
}

const isLuckyDrawMaxDrawChancesSingleTransactionValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDrawChancesSingleTransaction &&
    values.luckyDrawConfiguration.maxDrawChancesSingleTransaction > 0
  )
}

const isLuckyDrawMaxWinChancesPerClientValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxWinChancesPerClient &&
    values.luckyDrawConfiguration.maxWinChancesPerClient > 0
  )
}

const isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxWeeklyMaximumRewardsAllowed &&
    values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed > 0
  )
}

const isMaxPayoutValid = (values: CampaignDetailsFormValues) => {
  return (
    !isEmpty(values?.maxPayout) || (typeof values?.maxPayout === 'number' && values?.maxPayout <= 0)
  )
}

const isLuckyDraw = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return [MarketingCampaignType.LuckyDraw].includes(toTypeId(values.typeId))
}

const isCampaignOptions = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return [MarketingCampaignType.ClientRebate, MarketingCampaignType.ClientContest].includes(
    toTypeId(values.typeId)
  )
}

const isCampaignRebateOption = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return [MarketingCampaignType.ClientRebate].includes(toTypeId(values.typeId))
}

const toTypeId = (typeId: string | number) => {
  return isString(typeId) ? Number.parseInt(typeId, 10) : typeId
}
